import { makeid } from 'utils/helpers/makeid';

import {
  additionalFollowUpItem,
  freeTextCommentsItem,
  isPatientCROorDNCItem,
  needEscalateItem,
  twoWaySyncInfoItem,
  uniqueDaysItem,
} from '../common';

import type { TFormItem } from '../../types';

export const bloodPressure: TFormItem[] = [
  {
    key: makeid(10),
    title: (
      <p>
        Did the patient report any difficulties with blood pressure or pulse
        readings since the last call?
      </p>
    ),
    type: 'radio',
    required: false,
    name: 'difficultiesWithBPOrPulse',
    options: [
      {
        label:
          'Yes (describe in freetext; provide patient assistance if possible)',
        value: true,
        key: makeid(10),
      },
      { label: 'No', value: false, key: makeid(10) },
    ],
    items: [
      {
        type: 'textarea',
        name: 'difficultiesWithBPOrPulseComment',
        title: null,
        key: makeid(10),
      },
    ],
  },
  {
    key: makeid(10),
    title: (
      <p>
        Is the patient awaiting any device replacements, troubleshooting or
        supplies?
      </p>
    ),
    type: 'radio',
    required: false,
    name: 'waitingForDeviceReplacement',
    options: [
      {
        label: 'Yes',
        value: true,
        key: makeid(10),
        hideSubitemsIfParentNotSelected: true,
        subItems: [
          {
            type: 'checkbox',
            name: 'waitingForDeviceReplacementList',
            title: null,
            key: makeid(10),
            options: [
              {
                label: 'device replacement',
                value: 'device replacement',
                key: makeid(10),
              },
              {
                label: 'troubleshooting',
                value: 'troubleshooting',
                key: makeid(10),
              },
              { label: 'supplies', value: 'supplies', key: makeid(10) },
            ],
          },
        ],
      },
      {
        label:
          'No (describe in freetext; provide patient assistance if possible)',
        value: false,
        key: makeid(10),
        subItems: [
          {
            type: 'textarea',
            name: 'waitingForDeviceReplacementComment',
            title: null,
            key: makeid(10),
          },
        ],
      },
    ],
  },
  {
    key: makeid(10),
    title: null,
    type: 'empty',
    name: '_empty',
    items: [
      {
        key: makeid(10),
        title: (
          <p>
            Number of at-risk blood pressure readings reviewed: <span>__</span>
          </p>
        ),
        type: 'input-number',
        required: false,
        name: 'atRiskNumber',
        validation: { min: 0 },
      },
      {
        key: makeid(10),
        title: (
          <p>
            Highest systolic value: <span>__ mmHg</span>
          </p>
        ),
        type: 'input-number',
        required: false,
        name: 'highestSystolic',
        validation: { min: 0 },
      },
      {
        key: makeid(10),
        title: (
          <p>
            Lowest systolic value: <span>__ mmHg</span>
          </p>
        ),
        type: 'input-number',
        required: false,
        name: 'lowestSystolic',
        validation: { min: 0 },
      },
      {
        key: makeid(10),
        title: (
          <p>
            Highest diastolic value: <span>__ mmHg</span>
          </p>
        ),
        type: 'input-number',
        required: false,
        name: 'highestDiastolic',
        validation: { min: 0 },
      },
      {
        key: makeid(10),
        title: (
          <p>
            Lowest diastolic value: <span>__ mmHg</span>
          </p>
        ),
        type: 'input-number',
        required: false,
        name: 'lowestDiastolic',
        validation: { min: 0 },
      },
      {
        key: makeid(10),
        title: (
          <p>
            Highest pulse: <span>__ bpm</span>
          </p>
        ),
        type: 'input-number',
        required: false,
        name: 'highestPulse',
        validation: { min: 0 },
      },
      {
        key: makeid(10),
        title: (
          <p>
            Lowest pulse: <span>__ bpm</span>
          </p>
        ),
        type: 'input-number',
        required: false,
        name: 'lowestPulse',
        validation: { min: 0 },
      },
    ],
  },
  {
    key: makeid(10),
    title: <p>What type of at-risk trend(s) were observed?</p>,
    type: 'radio',
    name: 'typeofTrend',
    options: [
      {
        label: 'Trending high',
        value: 'high',
        key: makeid(10),
        hideSubitemsIfParentNotSelected: true,
        subItems: [
          {
            type: 'checkbox',
            name: 'highTrends',
            title: null,
            key: makeid(10),
            options: [
              { label: 'Systolic', value: 'systolic', key: makeid(10) },
              { label: 'Diastolic', value: 'diastolic', key: makeid(10) },
              {
                label: 'Pulse',
                value: 'pulse',
                key: makeid(10),
              },
            ],
          },
        ],
      },
      {
        label: 'Trending low',
        value: 'low',
        key: makeid(10),
        hideSubitemsIfParentNotSelected: true,
        subItems: [
          {
            type: 'checkbox',
            name: 'lowTrends',
            title: null,
            key: makeid(10),
            options: [
              { label: 'Systolic', value: 'systolic', key: makeid(10) },
              { label: 'Diastolic', value: 'diastolic', key: makeid(10) },
              {
                label: 'Pulse',
                value: 'pulse',
                key: makeid(10),
              },
            ],
          },
        ],
      },
      {
        label: 'No significant trends',
        value: 'none',
        key: makeid(10),
      },
    ],
  },
  {
    key: makeid(10),
    title: (
      <p>
        Compared to the last review, have the total number of at-risk readings:
      </p>
    ),
    type: 'radio',
    required: false,
    name: 'comparedToLastReview',
    options: [
      { label: 'Increased', value: 'increased', key: makeid(10) },
      { label: 'Decreased', value: 'decreased', key: makeid(10) },
      {
        label: 'Remained about the same',
        value: 'remained about the same',
        key: makeid(10),
      },
    ],
  },
  {
    key: makeid(10),
    title: (
      <p>
        Has the number or trend of at-risk readings changed compared to the last
        review?
      </p>
    ),
    type: 'radio',
    required: false,
    name: 'comparedToLastReviewTrend',
    options: [
      { label: 'Trend improving', value: 'improving', key: makeid(10) },
      { label: 'Trend worsening', value: 'worsening', key: makeid(10) },
      {
        label: 'No significant change',
        value: 'about the same',
        key: makeid(10),
      },
    ],
  },
  { ...uniqueDaysItem('blood pressure') },
  { ...twoWaySyncInfoItem },
  { ...isPatientCROorDNCItem },
  { ...additionalFollowUpItem },
  { ...needEscalateItem },
  { ...freeTextCommentsItem },
];
